import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  type GridToolbarProps,
} from "@mui/x-data-grid-premium";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { exportAutoTaggingPlans } from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { AI_PLAN_ADD_ITEMS_PATH } from "../constants";

type TaggingPlanTableToolbarProps = GridToolbarProps;

export default function TaggingPlanTableToolbar(_props: TaggingPlanTableToolbarProps) {
  const navigate = useNavigate();
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const canExportAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.export);
  const [exporting, setExporting] = useState(false);

  const gridToolbarFeatureButtonSlotProps = useMemo(
    () => ({
      button: {
        size: "small" as const,
        sx: {
          color: `${primaryColor} !important`,
          textTransform: "none",
          fontWeight: 500,
          "& .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26) !important",
            "& .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
            "& .MuiButton-startIcon .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
          },
        },
      },
    }),
    [primaryColor],
  );

  const handleExportAll = useCallback(async () => {
    if (exporting) return;

    setExporting(true);
    try {
      await exportAutoTaggingPlans();
      showMessage("Export started. Check your downloads.", "success");
    } catch (error) {
      showMessage(
        error instanceof Error ? error.message : "Failed to export assessment plan.",
        "error",
      );
    } finally {
      setExporting(false);
    }
  }, [exporting, showMessage]);

  return (
    <Box
      role="toolbar"
      sx={{
        p: "12px 0",
        bgcolor: "background.paper",
        boxShadow: "none",
        border: "none",
        borderBottom: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#262626", fontSize: "16px" }}
        >
          Tagging Plan
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          "& .MuiButton-root:not(.ai-plan-add-new-button)": {
            color: "#595959 !important",
            textTransform: "none",
            fontWeight: 500,
          },
          // Only tint SVG icons — do not use "*" or Badge label inherits button grey with !important
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: "inherit !important",
          },
        }}
      >
        {canManageAutoTagging ? (
          <Button
            variant="contained"
            size="small"
            className="ai-plan-add-new-button"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={brandContainedButtonSx}
            onClick={() => navigate(AI_PLAN_ADD_ITEMS_PATH)}
          >
            Add New
          </Button>
        ) : null}
        <GridToolbarDensitySelector slotProps={gridToolbarFeatureButtonSlotProps} />
        <GridToolbarColumnsButton slotProps={gridToolbarFeatureButtonSlotProps} />
        {canExportAutoTagging ? (
          <Tooltip
            title={exporting ? "Exporting…" : "Export all assessment plans."}
            arrow
            placement="top"
          >
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Button
                size="small"
                disabled={exporting}
                onClick={() => void handleExportAll()}
                sx={gridToolbarFeatureButtonSlotProps.button.sx}
                startIcon={
                  <FileDownloadOutlinedIcon sx={{ fontSize: "18px !important" }} />
                }
              >
                {exporting ? "Exporting…" : "Export"}
              </Button>
            </Box>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}
